import React, { useContext, useState } from "react";
import { PiChatCenteredText } from "react-icons/pi";
import { CgNotes } from "react-icons/cg";
import { SiOpenai } from "react-icons/si";
import { TbInfoHexagon } from "react-icons/tb";
import { BsCodeSlash } from "react-icons/bs";
import { MdOutlineDescription } from "react-icons/md";
import { sideNav } from "utils/constant";
import { ConditionContext } from "hooks/ConditionContext";
import { CommonContext } from "hooks/CommonContext";
import PropTypes from "prop-types";
import MonacoEditorPopup from "components/MonacoEditorPopup";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

  /* Fully override legacy class */
  .control-section {
    all: unset !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 2px !important;
    width: 72px !important;
    min-width: 72px !important;
    flex-shrink: 0 !important;
    height: 100% !important;
    overflow: hidden !important;
    background: #0b1120 !important;
    border-left: 1px solid #1a2540 !important;
    padding: 12px 0 !important;
    box-sizing: border-box !important;
  }

  .control-icon-box { all: unset !important; }

  /* ── Each nav button ── */
  .rs-btn {
    all: unset;
    box-sizing: border-box;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
    width: 56px;
    height: 58px;
    border-radius: 12px;
    cursor: pointer;
    /* Default: readable slate, NOT barely-visible grey */
    color: #7a8fb5;
    transition: background 0.15s, color 0.15s;
    flex-shrink: 0;
    font-family: 'Inter', system-ui, sans-serif;
  }

  .rs-btn:hover {
    background: rgba(34, 211, 238, 0.07);
    color: #c4d0ea;
  }

  /* Active — bright cyan tint, clearly obvious */
  .rs-btn.rs-active {
    background: rgba(34, 211, 238, 0.12);
    color: #22d3ee;
  }

  .rs-btn.rs-active .rs-icon {
    filter: drop-shadow(0 0 5px rgba(34, 211, 238, 0.45));
  }

  .rs-icon {
    font-size: 21px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Label — must be clearly legible on dark bg */
  .rs-label {
    font-size: 10.5px;
    font-weight: 500;
    letter-spacing: 0.02em;
    line-height: 1;
    color: inherit;
  }

  /* New message dot */
  .rs-dot {
    position: absolute;
    top: 7px;
    right: 7px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #f87171;
    border: 1.5px solid #0b1120;
  }

  /* Thin divider */
  .rs-sep {
    width: 32px;
    height: 1px;
    background: #1a2540;
    margin: 5px 0;
    flex-shrink: 0;
  }
`;

let _inj = false;
const inject = () => {
  if (_inj) return;
  const s = document.createElement("style");
  s.textContent = STYLES;
  document.head.appendChild(s);
  _inj = true;
};

const RightSidebar = () => {
  inject();
  const { userOwnData } = useContext(CommonContext);

  return (
    <div className="control-section">
      <SideBtn title="Chat"  navigator={sideNav.Chat}  icon={<PiChatCenteredText />} />
      <SideBtn title="Notes" navigator={sideNav.Notes} icon={<CgNotes />} />

      <div className="rs-sep" />

      {userOwnData.isCandidate ? (
        <CodeEditorBtn />
      ) : (
        <>
          <SideBtn title="Q&A" navigator={sideNav.QA}             icon={<SiOpenai />} />
          <SideBtn title="JD"  navigator={sideNav.JobDescription} icon={<MdOutlineDescription />} />
        </>
      )}

      <div className="rs-sep" />

      <SideBtn title="About" navigator={sideNav.About} icon={<TbInfoHexagon />} />
    </div>
  );
};

export default RightSidebar;

const SideBtn = ({ title, icon, navigator }) => {
  const { openedComponentDrawerSetter, openedComponentDrawer, interViewTypeToChat } =
    useContext(ConditionContext);
  const { isComeNewMessage, setIsComeNewMessage } = useContext(CommonContext);

  const isActive  = openedComponentDrawer === navigator;
  const hasNewMsg = navigator === sideNav.Chat && isComeNewMessage.length > 0;

  return (
    <button
      className={`rs-btn${isActive ? " rs-active" : ""}`}
      title={title}
      onClick={() => {
        openedComponentDrawerSetter(navigator);
        if (navigator === sideNav.Chat && interViewTypeToChat === isComeNewMessage) {
          setIsComeNewMessage("");
        }
      }}
    >
      {hasNewMsg && <span className="rs-dot" />}
      <span className="rs-icon">{icon}</span>
      <span className="rs-label">{title}</span>
    </button>
  );
};

SideBtn.propTypes = {
  title: PropTypes.string,
  icon: PropTypes.node,
  navigator: PropTypes.any,
};

const CodeEditorBtn = () => {
  const { openedComponentDrawer } = useContext(ConditionContext);
  const [show, setShow] = useState(false);

  return (
    <>
      <MonacoEditorPopup show={show} handleClose={() => setShow(false)} />
      <button
        className={`rs-btn${openedComponentDrawer === "editor" ? " rs-active" : ""}`}
        title="Code Editor"
        onClick={() => setShow(true)}
      >
        <span className="rs-icon"><BsCodeSlash /></span>
        <span className="rs-label">Editor</span>
      </button>
    </>
  );
};